import { useMemo, useState } from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import type { AnswerChannel } from "./awaitingInput";
import { parseToolBody } from "./parseToolBody";
import type { ToolCallEntry } from "./useTurns";

interface DeferredToolFallbackCardProps {
  entry: ToolCallEntry;
  // How the server takes this call's answer: free text for a tool return, an
  // approve/deny decision for an approval interrupt.
  channel: AnswerChannel;
  // Resume the paused run with the user's response. Omitted in read-only
  // views (shared / admin), where the card renders but cannot be answered.
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
}

// Generic card for a pending call with no bespoke entry in HITL_TOOL_RENDERERS,
// so the backend can ship a HITL tool ahead of its card without bricking the run.
// Once the call has a result (the staged answer, or the persisted one) it shows
// that as a receipt.
export const DeferredToolFallbackCard = ({
  entry,
  channel,
  onAnswer,
}: DeferredToolFallbackCardProps) => {
  const [text, setText] = useState("");
  const readOnly = onAnswer === undefined;
  const answered = entry.result !== undefined;

  // Show the raw arguments so the user has the context the missing card would
  // otherwise present. Pretty-print JSON; fall back to the verbatim string.
  const argsPreview = useMemo(() => {
    const view = parseToolBody(entry.args);
    if (view.kind === "json") return JSON.stringify(view.value, null, 2);
    if (view.kind === "text") return view.text;
    return null;
  }, [entry.args]);

  const answer = (value: string) => {
    if (onAnswer) void onAnswer(entry.id, value);
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-background p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <HelpCircle className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium text-foreground">
          The agent is waiting on <code className="text-xs">{entry.name}</code>
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {channel === "interrupt"
          ? "This tool has no custom form yet. Approve or deny it to continue."
          : "This tool has no custom form yet. Reply in plain text to continue."}
      </p>
      {argsPreview && (
        <pre className="max-h-40 overflow-auto rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
          {argsPreview}
        </pre>
      )}
      {answered ? (
        <span className="self-end text-xs italic text-muted-foreground">
          Answered: {entry.result}
        </span>
      ) : readOnly ? (
        <span className="self-end text-xs italic text-muted-foreground">Awaiting response</span>
      ) : channel === "interrupt" ? (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => answer("denied")}>
            Deny
          </Button>
          <Button size="sm" onClick={() => answer("approved")}>
            Approve
          </Button>
        </div>
      ) : (
        <>
          <Textarea
            autoFocus
            value={text}
            placeholder="Type your response…"
            onChange={(e) => setText(e.target.value)}
            className="min-h-20 text-sm"
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={() => answer(text.trim())} disabled={!text.trim()}>
              Submit
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
